import debounce from 'lodash-es/debounce'
import Image from 'next/legacy/image'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import smoothScroll from 'smoothscroll-polyfill'
import SvgArrow from '#/assets/images/carousel/arrow-forward.svg'
import SvgImage from '#/assets/images/carousel/no-image.svg'
import cn from '#/utils/classnames'
import styles from './styles.module.scss'

if (typeof window !== 'undefined') {
  smoothScroll.polyfill()
}

interface SwipableCarouselProps {
  autoPlay?: boolean
  count: number
  forceHttps: boolean
  images: string[]
  isMobile: boolean
  isScrolling: boolean
  onClick?(e: any): void
  onScroll?(e: React.UIEvent<HTMLDivElement>): void
}

const ImageLoadingPlaceholder = (w: number, h: number) => `
  <svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <defs>
      <linearGradient id="g">
        <stop stop-color="#333" offset="20%" />
        <stop stop-color="#222" offset="50%" />
        <stop stop-color="#333" offset="70%" />
      </linearGradient>
    </defs>
    <rect width="${w}" height="${h}" fill="#333" />
    <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
    <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
  </svg>`

function SwipableCarousel({
  autoPlay,
  count,
  forceHttps,
  images,
  isMobile,
  isScrolling,
  onClick,
  ...props
}: SwipableCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [playCount, setPlayCount] = useState(0)
  const refContainer = useRef<HTMLDivElement>(null)
  const [isLoading, setLoading] = useState(true)
  const imageLoaded = useRef<boolean[]>([])
  const items = useMemo(() => {
    const items = []
    for (let i = 0; i < count; ++i) {
      items[i] = images[i]
      if (items[i] && forceHttps) {
        if (items[i].startsWith('http')) {
          items[i] = items[i].replace('http:', 'https:')
        } else {
          items[i] = 'https:' + items[i]
        }
      }
    }
    return items
  }, [count, images, forceHttps])

  useEffect(() => {
    if (autoPlay) {
      const timer = setInterval(onAutoPlay, 1000)

      return () => {
        if (timer) {
          clearInterval(timer)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (autoPlay) {
      for (let i = 0; i < count; ++i) {
        imageLoaded.current[i] = false
      }
    }
  }, [count, autoPlay])

  useEffect(() => {
    if (playCount % 2 === 0) {
      // every 2s
      if (currentIndex === images.length - 1) {
        moveTo(0, 'auto')
      } else if (imageLoaded.current[currentIndex + 1]) onNext()
    }
  }, [playCount])

  useEffect(() => {
    setLoading(!imageLoaded.current[currentIndex])
  }, [currentIndex])

  function onAutoPlay() {
    setPlayCount(prev => prev + 1)
  }

  function moveTo(
    index: number,
    behavior: ScrollOptions['behavior'] = 'smooth'
  ) {
    if (!refContainer.current) return

    refContainer.current.scrollTo({
      left: index * refContainer.current.clientWidth,
      behavior
    })
  }

  function onPrev(e?: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    moveTo(Math.max(currentIndex - 1, 0))
  }

  function onNext(e?: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    moveTo(Math.min(currentIndex + 1, count - 1))
  }

  function onImageLoad(index: number) {
    imageLoaded.current[index] = true
    if (index === currentIndex) {
      setLoading(false)
    }

    if (autoPlay) {
      if (index === currentIndex + 1) {
        if (playCount % 2) setPlayCount(prev => prev + 1)
      }
    }
  }

  const handleScrollEnd = debounce(() => {
    if (!refContainer.current) return

    setCurrentIndex(
      Math.round(
        refContainer.current.scrollLeft / refContainer.current.clientWidth
      )
    )
  }, 100)

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    handleScrollEnd()

    if (props.onScroll) props.onScroll(e)
  }

  return (
    <div
      className={cn(styles['th-swipable-carousel-container'], { [styles['th-mobile']]: isMobile })}
      onClick={onClick}
    >
      <div
        ref={refContainer}
        className={styles['th-swipable-carousel']}
        style={{ overflow: isMobile ? 'auto' : 'hidden' }}
        onScroll={onScroll}
      >
        {items.length ? (
          (isScrolling ? [items[0]] : items).map((item, index) => (
            item ? (
              <div key={index} className={styles['th-image-container']}>
                <Image
                  alt={`Photo ${index}`}
                  blurDataURL={`data:image/svg+xml;base64,${btoa(ImageLoadingPlaceholder(700, 475))}`}
                  layout='fill'
                  lazyRoot={refContainer}
                  objectFit='cover'
                  placeholder='blur'
                  priority={autoPlay && index <= Math.min(currentIndex + 1, items.length - 1)}
                  src={item}
                  onLoadingComplete={() => onImageLoad(index)}
                />
                {isLoading && <SvgImage className='z-10 h-11 w-10' />}
              </div>
            ) : (
              <div key={index} className={cn(styles['th-image-container'], styles['th-placeholder'])}>
                <SvgImage className='z-10 h-11 w-10' />
              </div>
            )
          ))
        ) : (
          <div className={styles['th-image-container']} />
        )}
      </div>
      {count > 1 && (
        <div className={styles['th-carousel-actions']}>
          <div onClick={onPrev}>
            <SvgArrow
              className={cn(styles['th-action-left'], { [styles['th-disabled']]: currentIndex <= 0 })}
            />
          </div>
          <div onClick={onNext}>
            <SvgArrow
              className={cn(styles['th-action-right'], { [styles['th-disabled']]: currentIndex >= count - 1 })}
            />
          </div>
        </div>
      )}
    </div>
  )
}

SwipableCarousel.defaultProps = {
  isScrolling: false,
  isMobile: false,
  forceHttps: true
}

export default memo(SwipableCarousel)
